import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Badge, Button, Card, Col, Row, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import L from 'leaflet';
import { MapContainer, Marker, Popup, TileLayer, useMapEvents } from 'react-leaflet';
import MarkerClusterGroup from '@changey/react-leaflet-markercluster';
import 'leaflet/dist/leaflet.css';
import '@changey/react-leaflet-markercluster/dist/styles.min.css';

const SALES_MARKER_ICON = L.divIcon({
  className: 'gestodo-sales-map-marker',
  html: `
    <div style="
      width: 18px;
      height: 18px;
      border-radius: 999px;
      background: #2c7be5;
      border: 3px solid rgba(255,255,255,0.95);
      box-shadow: 0 6px 14px rgba(44,123,229,0.35);
    "></div>
  `,
  iconSize: [18, 18],
  iconAnchor: [9, 9]
});

const formatNumber = value => Number(value || 0).toLocaleString();

const formatAmount = (value, currencyId) => {
  const numericValue = Number(value || 0);
  if (!currencyId) {
    return numericValue.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  }

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyId,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numericValue);
  } catch (error) {
    return `${currencyId} ${numericValue.toFixed(0)}`;
  }
};

const formatDateTime = (value, fallback) => {
  if (!value) return fallback;
  const parsedValue = new Date(value);
  if (Number.isNaN(parsedValue.getTime())) return value;
  return parsedValue.toLocaleString();
};

const formatCompactCount = value => {
  const numericValue = Number(value || 0);
  if (numericValue < 1000) {
    return `${numericValue}`;
  }
  if (numericValue < 10000) {
    return `${(numericValue / 1000).toFixed(1)}k`;
  }
  return `${Math.round(numericValue / 1000)}k`;
};

const clampRatio = value => Math.max(0, Math.min(1, Number(value || 0)));

const getClusterPalette = ratio => {
  const normalizedRatio = clampRatio(ratio);
  const hue = 210 - normalizedRatio * 190;
  const saturation = 84;
  const lightness = 50 - normalizedRatio * 8;
  const ringLightness = 72 - normalizedRatio * 18;

  return {
    solid: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
    ring: `hsla(${hue}, ${saturation}%, ${ringLightness}%, 0.34)`,
    shadow: `hsla(${hue}, ${saturation}%, ${Math.max(lightness - 4, 28)}%, 0.34)`
  };
};

const buildSalesClusterIcon = (salesWeight, visibleSalesWeight) => {
  const baseWeight = Math.max(Number(visibleSalesWeight || 0), Number(salesWeight || 0), 1);
  const ratio = clampRatio(Number(salesWeight || 0) / baseWeight);
  const palette = getClusterPalette(ratio);
  const size = Math.round(40 + Math.min(18, ratio * 18 + Math.log(Number(salesWeight || 0) + 1) * 2.4));

  return L.divIcon({
    className: 'gestodo-sales-cluster-icon-wrapper',
    html: `
      <div style="
        width:${size}px;
        height:${size}px;
        border-radius:999px;
        background:${palette.ring};
        display:flex;
        align-items:center;
        justify-content:center;
        box-shadow:0 10px 24px ${palette.shadow};
      ">
        <div style="
          width:${size - 8}px;
          height:${size - 8}px;
          border-radius:999px;
          background:${palette.solid};
          color:#fff;
          display:flex;
          flex-direction:column;
          align-items:center;
          justify-content:center;
          font-weight:700;
          line-height:1;
          border:3px solid rgba(255,255,255,0.96);
        ">
          <span style="font-size:${size >= 52 ? 13 : 12}px;">${formatCompactCount(salesWeight)}</span>
          <span style="font-size:10px; margin-top:3px; opacity:0.92;">${Math.round(ratio * 100)}%</span>
        </div>
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
};

const getVisibleStats = (points, bounds) => {
  const initialValue = {
    visibleSalesWeight: 0,
    visibleLocationsCount: 0,
    bounds: bounds
      ? {
          south: bounds.getSouth(),
          west: bounds.getWest(),
          north: bounds.getNorth(),
          east: bounds.getEast()
        }
      : null
  };

  if (!bounds) {
    return points.reduce((accumulator, point) => ({
      visibleSalesWeight:
        accumulator.visibleSalesWeight + Number(point.orders_count || 0),
      visibleLocationsCount: accumulator.visibleLocationsCount + 1
    }), initialValue);
  }

  return points.reduce((accumulator, point) => {
    if (!bounds.contains([point.latitude, point.longitude])) {
      return accumulator;
    }

    return {
      visibleSalesWeight:
        accumulator.visibleSalesWeight + Number(point.orders_count || 0),
      visibleLocationsCount: accumulator.visibleLocationsCount + 1
    };
  }, initialValue);
};

const SalesMapController = ({ points, onViewportChange }) => {
  const updateViewportStats = useCallback(
    mapInstance => {
      const bounds = mapInstance?.getBounds?.();
      if (!bounds) return;
      onViewportChange(getVisibleStats(points, bounds));
    },
    [onViewportChange, points]
  );

  const map = useMapEvents({
    moveend() {
      updateViewportStats(map);
    },
    zoomend() {
      updateViewportStats(map);
    }
  });

  // Force Leaflet to recalculate size after mount so it works correctly
  // when rendered inside a tab that was hidden during initial paint.
  useEffect(() => {
    if (!map) return;
    const timer = setTimeout(() => {
      map.invalidateSize({ animate: false });
    }, 50);
    return () => clearTimeout(timer);
  }, [map]);

  useEffect(() => {
    if (!map || !points.length) return;

    // Ensure size is correct before fitting bounds (tab may have just become visible).
    map.invalidateSize({ animate: false });

    if (points.length === 1) {
      map.setView([points[0].latitude, points[0].longitude], 11, {
        animate: false
      });
      updateViewportStats(map);
      return;
    }

    const bounds = L.latLngBounds(
      points.map(point => [point.latitude, point.longitude])
    );
    map.fitBounds(bounds, {
      padding: [28, 28],
      maxZoom: 12,
      animate: false
    });
    updateViewportStats(map);
  }, [map, points, updateViewportStats]);

  return null;
};

const SalesLocationMapCard = ({ points, topLocations, currencyId, t }) => {
  const [viewportStats, setViewportStats] = useState({
    visibleSalesWeight: 0,
    visibleLocationsCount: 0,
    bounds: null
  });
  const normalizedPoints = useMemo(
    () =>
      (points || [])
        .map(point => ({
          ...point,
          latitude: Number(point.latitude),
          longitude: Number(point.longitude)
        }))
        .filter(
          point =>
            Number.isFinite(point.latitude) &&
            Number.isFinite(point.longitude)
        ),
    [points]
  );
  const normalizedTopLocations = useMemo(() => topLocations || [], [topLocations]);
  const totalSalesWeight = useMemo(
    () =>
      normalizedPoints.reduce(
        (total, point) => total + Number(point.orders_count || 0),
        0
      ),
    [normalizedPoints]
  );
  const clusterRenderKey = `${viewportStats.visibleSalesWeight}-${viewportStats.visibleLocationsCount}-${normalizedPoints.length}-${viewportStats.bounds ? `${viewportStats.bounds.south}:${viewportStats.bounds.west}:${viewportStats.bounds.north}:${viewportStats.bounds.east}` : 'full'}`;
  const clusterIconCreateFunction = useCallback(
    cluster => {
      const childMarkers = cluster.getAllChildMarkers();
      const viewportBounds = viewportStats.bounds
        ? L.latLngBounds(
            [viewportStats.bounds.south, viewportStats.bounds.west],
            [viewportStats.bounds.north, viewportStats.bounds.east]
          )
        : null;
      const visibleSalesWeight = Math.max(
        Number(viewportStats.visibleSalesWeight || 0),
        Number(totalSalesWeight || 0),
        1
      );

      const clusterSalesWeight = childMarkers.reduce(
        (total, marker) => {
          if (viewportBounds && !viewportBounds.contains(marker.getLatLng())) {
            return total;
          }
          return total + Number(marker.options.salesWeight || 0);
        },
        0
      );

      return buildSalesClusterIcon(
        clusterSalesWeight,
        Number(viewportStats.visibleSalesWeight || 0) || visibleSalesWeight
      );
    },
    [totalSalesWeight, viewportStats.bounds, viewportStats.visibleSalesWeight]
  );

  return (
    <Card className="border-0 shadow-sm h-100">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
          <div>
            <h5 className="mb-1">{t('DASHBOARD_HOME.SECTIONS.SALES_MAP')}</h5>
            <div className="text-600 fs--1">
              {t('DASHBOARD_HOME.SECTIONS.SALES_MAP_HELP')}
            </div>
          </div>
          {normalizedPoints.length ? (
            <div className="d-flex flex-wrap gap-2 justify-content-end">
              <Badge bg="primary" pill>
                {t('DASHBOARD_HOME.TABLE.SALES_COUNT')}: {formatNumber(
                  totalSalesWeight
                )}
              </Badge>
              <Badge bg="info" pill>
                {t('DASHBOARD_HOME.FIELDS.MAPPED_LOCATIONS')}: {formatNumber(
                  normalizedPoints.length
                )}
              </Badge>
            </div>
          ) : null}
        </div>

        {normalizedPoints.length || normalizedTopLocations.length ? (
          <Row className="g-3">
            <Col xs={12}>
              {normalizedPoints.length ? (
                <div
                  className="border rounded overflow-hidden"
                  style={{ height: 520 }}
                  dir="ltr"
                >
                  <MapContainer
                    center={[-34.6037, -58.3816]}
                    zoom={5}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={false}
                  >
                    <TileLayer
                      attribution='&copy; OpenStreetMap contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <SalesMapController
                      points={normalizedPoints}
                      onViewportChange={setViewportStats}
                    />
                    <MarkerClusterGroup
                      key={clusterRenderKey}
                      chunkedLoading
                      spiderfyOnMaxZoom={false}
                      showCoverageOnHover={false}
                      maxClusterRadius={52}
                      iconCreateFunction={clusterIconCreateFunction}
                    >
                      {normalizedPoints.map(point => (
                        <Marker
                          key={point.id}
                          position={[point.latitude, point.longitude]}
                          icon={SALES_MARKER_ICON}
                          salesWeight={Number(point.orders_count || 0)}
                        >
                          <Popup>
                            <div className="fw-semi-bold mb-1">{point.label}</div>
                            {point.location_line ? (
                              <div className="text-600 fs--1 mb-2">
                                {point.location_line}
                              </div>
                            ) : null}
                            <div className="fs--1">
                              {t('DASHBOARD_HOME.TABLE.SALES_COUNT')}: {formatNumber(point.orders_count || 0)}
                            </div>
                            <div className="fs--1 mb-2">
                              {t('DASHBOARD_HOME.TABLE.TOTAL')}: {formatAmount(point.total_amount, currencyId)}
                            </div>
                            {point.latest_order_id ? (
                              <Button
                                as={Link}
                                to={`/sales/${point.latest_order_id}`}
                                size="sm"
                                variant="primary"
                              >
                                {t('DASHBOARD_HOME.ACTIONS.OPEN')}
                              </Button>
                            ) : null}
                          </Popup>
                        </Marker>
                      ))}
                    </MarkerClusterGroup>
                  </MapContainer>
                </div>
              ) : (
                <div
                  className="border rounded d-flex align-items-center justify-content-center text-center text-600 px-4"
                  style={{ height: 520 }}
                >
                  {t('DASHBOARD_HOME.EMPTY.SALES_MAP')}
                </div>
              )}
            </Col>

            {normalizedTopLocations.length ? (
              <Col xs={12}>
                <Table responsive className="mb-0 fs--1">
                  <thead className="bg-light">
                    <tr>
                      <th>{t('DASHBOARD_HOME.TABLE.LOCATION')}</th>
                      <th>{t('DASHBOARD_HOME.TABLE.SALES_COUNT')}</th>
                      <th>{t('DASHBOARD_HOME.TABLE.TOTAL')}</th>
                      <th>{t('DASHBOARD_HOME.TABLE.AVG_TICKET')}</th>
                      <th>{t('DASHBOARD_HOME.TABLE.CANCELLATION_RATE')}</th>
                      <th>{t('DASHBOARD_HOME.TABLE.LAST_ORDER')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {normalizedTopLocations.slice(0, 10).map(point => (
                      <tr key={`table-${point.id}`}>
                        <td>
                          <div className="fw-semi-bold">{point.label}</div>
                          {point.location_line ? (
                            <div className="text-600 fs--2">{point.location_line}</div>
                          ) : null}
                        </td>
                        <td>{formatNumber(point.orders_count || 0)}</td>
                        <td>{formatAmount(point.total_amount, currencyId)}</td>
                        <td>{formatAmount(point.avg_ticket, currencyId)}</td>
                        <td>{Math.round(Number(point.cancelled_rate || 0) * 100)}%</td>
                        <td>
                          {point.latest_order_id ? (
                            <Link to={`/sales/${point.latest_order_id}`}>
                              {formatDateTime(
                                point.latest_order_at,
                                t('DASHBOARD_HOME.SYNC.NEVER')
                              )}
                            </Link>
                          ) : (
                            t('DASHBOARD_HOME.SYNC.NEVER')
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Col>
            ) : null}
          </Row>
        ) : (
          <div className="text-600">{t('DASHBOARD_HOME.EMPTY.SALES_MAP')}</div>
        )}
      </Card.Body>
    </Card>
  );
};

SalesMapController.propTypes = {
  points: PropTypes.arrayOf(PropTypes.object).isRequired,
  onViewportChange: PropTypes.func.isRequired
};

SalesLocationMapCard.propTypes = {
  points: PropTypes.arrayOf(PropTypes.object),
  topLocations: PropTypes.arrayOf(PropTypes.object),
  currencyId: PropTypes.string,
  t: PropTypes.func.isRequired
};

SalesLocationMapCard.defaultProps = {
  points: [],
  topLocations: [],
  currencyId: ''
};

export default SalesLocationMapCard;
